export type CommandCategory = 'security' | 'moderation' | 'fun' | 'utility';

export interface Command {
  id: string;
  name: string;
  syntax: string;
  description: string;
  category: CommandCategory;
  example?: string;
}

export const commands: Command[] = [
  // Security Commands
  {
    id: 'lock',
    name: 'Lock',
    syntax: ',lock / /lock',
    description: 'Lock the channel to prevent users from sending messages.',
    category: 'security',
    example: ',lock',
  },
  {
    id: 'unlock',
    name: 'Unlock',
    syntax: ',unlock / /unlock',
    description: 'Unlock the channel to allow users to send messages.',
    category: 'security',
    example: ',unlock',
  },
  {
    id: 'antinuke',
    name: 'Antinuke',
    syntax: ',antinuke ... / /antinuke ...',
    description: 'Antinuke management — setup, remove, whitelist <user>, info. Protects your server from mass destructive actions.',
    category: 'security',
    example: ',antinuke setup',
  },
  {
    id: 'antiraid',
    name: 'Antiraid',
    syntax: ',antiraid ... / /antiraid ...',
    description: 'Antiraid controls to prevent mass join attacks and protect your server.',
    category: 'security',
    example: ',antiraid on',
  },

  // Moderation Commands
  {
    id: 'kick',
    name: 'Kick',
    syntax: ',kick / /kick',
    description: 'Kick a member from the server.',
    category: 'moderation',
    example: ',kick @user Breaking rules',
  },
  {
    id: 'ban',
    name: 'Ban',
    syntax: ',ban / /ban',
    description: 'Ban a member from the server.',
    category: 'moderation',
    example: ',ban @spammer',
  },
  {
    id: 'timeout',
    name: 'Timeout',
    syntax: ',timeout / ,to / /timeout',
    description: 'Timeout a member, preventing them from interacting with the server temporarily.',
    category: 'moderation',
    example: ',timeout @user 10m',
  },
  {
    id: 'untimeout',
    name: 'Untimeout',
    syntax: ',untimeout / /untimeout',
    description: 'Remove timeout from a member.',
    category: 'moderation',
    example: ',untimeout @user',
  },
  {
    id: 'purge',
    name: 'Purge',
    syntax: ',purge / /purge',
    description: 'Bulk delete messages in the current channel.',
    category: 'moderation',
    example: ',purge 50',
  },
  {
    id: 'clear',
    name: 'Clear',
    syntax: ',clear / ,c / /clear',
    description: 'Delete messages from a specific user in the channel.',
    category: 'moderation',
    example: ',clear @user 20',
  },
  {
    id: 'warn',
    name: 'Warn',
    syntax: ',warn / /warn',
    description: 'Warn a user for breaking server rules.',
    category: 'moderation',
    example: ',warn @user Inappropriate language',
  },
  {
    id: 'history',
    name: 'History',
    syntax: ',history @user',
    description: 'Show moderation history including warnings and removal actions (prefix only).',
    category: 'moderation',
    example: ',history @user',
  },
  {
    id: 'logs',
    name: 'Logs',
    syntax: '/logs',
    description: 'Legacy slash entry for warns — prefix ,logs was replaced by ,history.',
    category: 'moderation',
    example: '/logs',
  },
  {
    id: 'nuke',
    name: 'Nuke',
    syntax: ',nuke',
    description: 'Duplicate and replace the channel (requires Admin + ManageChannels permissions).',
    category: 'moderation',
    example: ',nuke',
  },
  {
    id: 'filter',
    name: 'Filter',
    syntax: ',filter / /filter',
    description: 'Manage word filters — add, remove, or list filtered words.',
    category: 'moderation',
    example: ',filter add badword',
  },

  // Fun Commands
  {
    id: 'tictactoe',
    name: 'Tic-Tac-Toe',
    syntax: ',tictactoe @user / /tictactoe',
    description: 'Play Tic-Tac-Toe with another user.',
    category: 'fun',
    example: ',tictactoe @friend',
  },
  {
    id: 'blacktea',
    name: 'Blacktea',
    syntax: ',blacktea / /blacktea',
    description: 'Play the Blacktea game.',
    category: 'fun',
    example: ',blacktea',
  },
  {
    id: '8ball',
    name: '8Ball',
    syntax: ',8ball <question> / /8ball question',
    description: 'Ask the magic 8-ball a question and receive a mystical answer.',
    category: 'fun',
    example: ',8ball Will I win?',
  },
  {
    id: 'coinflip',
    name: 'Coin Flip',
    syntax: ',coinflip / ,flip',
    description: 'Flip a coin - heads or tails!',
    category: 'fun',
    example: ',coinflip',
  },
  {
    id: 'roll',
    name: 'Roll Dice',
    syntax: ',roll [sides]',
    description: 'Roll dice with specified number of sides (default 6).',
    category: 'fun',
    example: ',roll 20',
  },
  {
    id: 'meme',
    name: 'Meme',
    syntax: ',meme',
    description: 'Get a random meme from meme-api.com.',
    category: 'fun',
    example: ',meme',
  },

  // Utility Commands
  {
    id: 'cmds',
    name: 'Commands',
    syntax: ',cmds / /cmds',
    description: 'Display help pages with all available commands.',
    category: 'utility',
    example: ',cmds',
  },
  {
    id: 'ping',
    name: 'Ping',
    syntax: ',ping / /ping',
    description: 'Check the bot latency and response time.',
    category: 'utility',
    example: ',ping',
  },
  {
    id: 'help',
    name: 'Help',
    syntax: ',help / /help',
    description: 'Get the support server invite link.',
    category: 'utility',
    example: ',help',
  },
  {
    id: 'sav',
    name: 'Show Avatar',
    syntax: ',sav [user] / /sav user',
    description: 'Show user avatar in full size.',
    category: 'utility',
    example: ',sav @user',
  },
  {
    id: 'avatar',
    name: 'Avatar',
    syntax: ',avatar @user / ,av',
    description: 'Show user avatar URL (prefix only for ,av alias).',
    category: 'utility',
    example: ',avatar @user',
  },
  {
    id: 'whois',
    name: 'Whois',
    syntax: ',whois @user / ,userinfo @user / /whois',
    description: 'Display user info embed with detailed information.',
    category: 'utility',
    example: ',whois @user',
  },
  {
    id: 'serverinfo',
    name: 'Server Info',
    syntax: ',si / /si',
    description: 'Display detailed server information.',
    category: 'utility',
    example: ',si',
  },
  {
    id: 'prefix',
    name: 'Prefix',
    syntax: ',prefix [value] / /prefix value',
    description: 'View or set the server prefix (Admin only).',
    category: 'utility',
    example: ',prefix !',
  },
  {
    id: 'role',
    name: 'Role',
    syntax: ',role / /role',
    description: 'Role management — create <name>, assign <user> <role_name>.',
    category: 'utility',
    example: ',role create Member',
  },
  {
    id: 'autoresponder',
    name: 'Autoresponder',
    syntax: ',autoresponder / /autoresponder',
    description: 'Manage auto triggers — add <trigger> <response>, remove <trigger>, list.',
    category: 'utility',
    example: ',autoresponder add hello Hi there!',
  },
  {
    id: 'afk',
    name: 'AFK',
    syntax: ',afk / /afk',
    description: 'Set your AFK status with an optional message.',
    category: 'utility',
    example: ',afk Away for lunch',
  },
  {
    id: 'forcenickname',
    name: 'Force Nickname',
    syntax: ',fn @user <nick> / /fn',
    description: 'Force a nickname on a user.',
    category: 'utility',
    example: ',fn @user NewName',
  },
  {
    id: 'snipe',
    name: 'Snipe',
    syntax: ',s / ,snipe / /s',
    description: 'Snipe the last deleted message in the channel.',
    category: 'utility',
    example: ',snipe',
  },
  {
    id: 'clearsnipe',
    name: 'Clear Snipe',
    syntax: ',cs / ,clearsnipe',
    description: 'Clear the snipe cache for the channel.',
    category: 'utility',
    example: ',cs',
  },
  {
    id: 'search',
    name: 'Search',
    syntax: ',search / /search',
    description: 'Instant web search powered by search engines.',
    category: 'utility',
    example: ',search Discord bots',
  },
  {
    id: 'imagesearch',
    name: 'Image Search',
    syntax: ',imagesearch / /imagesearch',
    description: 'Search for images on the web.',
    category: 'utility',
    example: ',imagesearch cute cats',
  },
  {
    id: 'translate',
    name: 'Translate',
    syntax: ',translate / /translate',
    description: 'Translate a word or phrase to another language.',
    category: 'utility',
    example: ',translate hello spanish',
  },
];

export interface FAQ {
  question: string;
  answer: string;
}

export const faqs: FAQ[] = [
  {
    question: 'Is Yokki completely free?',
    answer: 'Yes! Yokki is 100% free with no premium tiers or hidden costs. All features including antinuke protection, moderation tools, and fun games are available to everyone.',
  },
  {
    question: 'How do I add Yokki to my server?',
    answer: 'Click the "Add to Discord" button on this page, select your server from the dropdown, and authorize the required permissions. Yokki will be instantly added and ready to use!',
  },
  {
    question: 'What permissions does Yokki need?',
    answer: 'Yokki requires administrator permissions to function properly, especially for moderation and antinuke features. This allows it to manage roles, channels, and take protective actions when needed.',
  },
  {
    question: 'How does the antinuke system work?',
    answer: 'The antinuke system monitors important server events (joins, leaves, bans, role/channel changes) and logs them to a dedicated channel. You can whitelist trusted admins to prevent false alerts. Note: it records and alerts but does not automatically ban users.',
  },
  {
    question: 'Can I customize Yokki\'s prefix?',
    answer: 'Yes! Use the ,prefix command to view or set a custom server prefix. By default, Yokki uses the comma (,) prefix for all commands.',
  },
  {
    question: 'Does Yokki work on mobile Discord?',
    answer: 'Yes! All of Yokki\'s commands work perfectly on Discord\'s mobile app. Interactive features like Tic-Tac-Toe use Discord\'s native buttons which work great on mobile.',
  },
  {
    question: 'How do I get support or report bugs?',
    answer: 'Join our support Discord server (link in footer) where our team and community can help you. You can also report bugs, request features, and get setup assistance there.',
  },
  {
    question: 'Is my server data safe with Yokki?',
    answer: 'Absolutely. Yokki only stores essential data needed for functionality (like warnings and autoresponders). We never share your data with third parties and follow Discord\'s privacy guidelines.',
  },
];

export interface Competitor {
  name: string;
  setup: string;
  price: string;
  protection: string;
  allInOne: string;
}

export const competitors: Competitor[] = [
  {
    name: 'Yokki',
    setup: 'Quick & Easy',
    price: '100% Free',
    protection: 'Advanced',
    allInOne: 'Yes',
  },
  {
    name: 'MEE6',
    setup: 'Moderate',
    price: 'Premium $11.95/mo',
    protection: 'Limited',
    allInOne: 'Partial',
  },
  {
    name: 'Dyno',
    setup: 'Moderate',
    price: 'Premium $9.99/mo',
    protection: 'Basic',
    allInOne: 'Partial',
  },
  {
    name: 'Carl-bot',
    setup: 'Complex',
    price: 'Free',
    protection: 'Minimal',
    allInOne: 'No',
  },
];
